import { useParams } from 'react-router-dom';
import { realLinkProps } from '../components/links/realLinkProps';
import { useRouterNavigate } from '../components/links/useEntityLinkProps';
import { useTranslation } from 'react-i18next';
import {
  makeStyles,
  tokens,
  Text,
  Button,
  Spinner,
  MessageBar,
  MessageBarBody,
} from '@fluentui/react-components';
import {
  TaskListSquareLtrRegular,
  ArrowLeftRegular,
  DocumentBulletListRegular,
  AlertRegular,
} from '@fluentui/react-icons';
import {
  useStateTaxClient,
  useFilingHealthByClient,
} from '../services/stateTaxClientsApi';
import { monthsAgo } from '../utils/dateMath';
import { FieldList } from './state-tax-client-detail/FieldList';
import { IdentifierList } from './state-tax-client-detail/IdentifierList';
import { CommunicationsCard } from './state-tax-client-detail/CommunicationsCard';
import { FilingActivityCard } from './state-tax-client-detail/FilingActivityCard';
import { BillingCard } from './state-tax-client-detail/BillingCard';
import { NotesCard } from './state-tax-client-detail/NotesCard';
import { ObligationEventsCard } from './state-tax-client-detail/ObligationEventsCard';
import { ObligationsCard } from './state-tax-client-detail/ObligationsCard';
import type { FieldRow } from './state-tax-client-detail/types';
import type { StateTaxClientSummary } from '../types';
import { apiErrorMessage } from '../lib/apiErrorMessage';
import ClientIdentityBlock from '../components/common/ClientIdentityBlock';
import { pageShell } from '../components/layout/pageShell';

const useStyles = makeStyles({
  page: {
    ...pageShell,
  },
  backRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  title: {
    fontSize: tokens.fontSizeHero800,
    fontWeight: tokens.fontWeightSemibold,
    lineHeight: '1.1',
  },
  subtitle: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase300,
  },
  cardGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
    gap: tokens.spacingHorizontalL,
    alignItems: 'start',
  },
  cardHeading: {
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    marginBottom: tokens.spacingVerticalM,
    paddingBottom: tokens.spacingVerticalS,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  fieldGrid: {
    display: 'grid',
    gridTemplateColumns: 'minmax(140px, max-content) 1fr',
    columnGap: tokens.spacingHorizontalL,
    rowGap: tokens.spacingVerticalS,
    alignItems: 'baseline',
  },
  fieldLabel: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  fieldValue: {
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground1,
  },
  emptyHint: {
    color: tokens.colorNeutralForeground3,
    fontStyle: 'italic',
    fontSize: tokens.fontSizeBase200,
  },
  actions: {
    display: 'flex',
    gap: tokens.spacingHorizontalM,
    flexWrap: 'wrap',
  },
  obligationsCard: {
    width: '100%',
  },
  obligationsHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: tokens.spacingVerticalM,
    paddingBottom: tokens.spacingVerticalS,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    gap: tokens.spacingHorizontalM,
    flexWrap: 'wrap',
  },
  notesCell: {
    whiteSpace: 'pre-wrap',
    maxWidth: '320px',
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
  },
  derivedRow: {
    backgroundColor: tokens.colorNeutralBackground2,
  },
  clickableRow: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  derivedFootnote: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    fontStyle: 'italic',
    marginTop: tokens.spacingVerticalS,
  },
  suggestionsCard: {
    width: '100%',
    backgroundColor: tokens.colorPaletteYellowBackground1,
    border: `1px solid ${tokens.colorPaletteYellowBorder1}`,
  },
  suggestionItem: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalM,
    padding: `${tokens.spacingVerticalS} 0`,
    borderTop: `1px solid ${tokens.colorPaletteYellowBorder2}`,
  },
  suggestionItemFirst: {
    borderTop: 'none',
  },
  suggestionMeta: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  suggestionTitle: {
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase300,
  },
  suggestionDetails: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  suggestionFiles: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    fontStyle: 'italic',
  },
  // Tagged-identifier styling — a pill with a monospaced value and a copy affordance.
  // Used for EIN / sales tax cert / business partner # / etc. on the Identifiers card.
  identifierTag: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '3px 4px 3px 10px',
    borderRadius: '6px',
    backgroundColor: tokens.colorNeutralBackground3,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    fontFamily: tokens.fontFamilyMonospace,
    fontSize: tokens.fontSizeBase200,
    fontVariantNumeric: 'tabular-nums',
    color: tokens.colorNeutralForeground1,
    transition: 'background-color 100ms ease-in',
  },
  identifierTagCopied: {
    backgroundColor: tokens.colorPaletteGreenBackground2,
    border: `1px solid ${tokens.colorPaletteGreenBorder1}`,
  },
  copyButton: {
    minWidth: '24px',
    width: '24px',
    height: '24px',
    padding: '0',
    border: 'none',
    background: 'transparent',
    color: tokens.colorNeutralForeground3,
    cursor: 'pointer',
    borderRadius: '4px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'color 100ms ease-in, background-color 100ms ease-in',
    '&:hover': {
      color: tokens.colorNeutralForeground1,
      backgroundColor: tokens.colorNeutralBackground1,
    },
  },
  // F2 — wraps the Active/Inactive Badge in an unstyled button so it works as
  // a PopoverTrigger without the default browser button chrome interfering with
  // the Badge's own styling.
  statusBadgeButton: {
    background: 'none',
    border: 'none',
    padding: 0,
    margin: 0,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
  },
  obligationHistoryList: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    minWidth: '320px',
    maxWidth: '420px',
    maxHeight: '400px',
    overflowY: 'auto',
  },
  obligationHistoryRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    padding: `${tokens.spacingVerticalXS} 0`,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    '&:first-child': {
      borderTop: 'none',
      paddingTop: 0,
    },
  },
  obligationHistoryHeader: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalS,
  },
  obligationHistoryTime: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    fontVariantNumeric: 'tabular-nums',
  },
  obligationHistoryDelta: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
  },
  obligationHistoryReason: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    fontStyle: 'italic',
  },
  // Filing-history strip — a small horizontal row of cells, one per period.
  // Reads right-to-left (most recent on the right) so "today" is at the end,
  // matching how staff thinks about a timeline.
  historyStrip: {
    display: 'inline-flex',
    flexDirection: 'row-reverse', // most-recent on the right
    gap: '2px',
    alignItems: 'center',
  },
  historyCell: {
    display: 'inline-block',
    width: '14px',
    height: '14px',
    borderRadius: '3px',
    cursor: 'help',
    transition: 'transform 80ms ease-in',
    '&:hover': {
      transform: 'scale(1.2)',
    },
  },
  historyCellFiled: {
    backgroundColor: tokens.colorPaletteGreenBackground3,
    border: `1px solid ${tokens.colorPaletteGreenBorder1}`,
  },
  historyCellMissing: {
    backgroundColor: tokens.colorPaletteRedBackground2,
    border: `1px solid ${tokens.colorPaletteRedBorder1}`,
  },
  historyCellPreEngagement: {
    backgroundColor: tokens.colorNeutralBackground3,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  // Filing-history cells double as Popover triggers — click-to-expand needs a
  // hover affordance plus a clear "click me" cursor.
  historyCellButton: {
    cursor: 'pointer',
    background: 'transparent',
    padding: 0,
    margin: 0,
  },
  historyPopover: {
    minWidth: '280px',
    maxWidth: '360px',
    padding: tokens.spacingVerticalM,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  historyPopHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalS,
  },
  historyPopTitle: {
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightSemibold,
  },
  historyPopMeta: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  historyPopFile: {
    fontFamily: tokens.fontFamilyMonospace,
    fontSize: tokens.fontSizeBase200,
    backgroundColor: tokens.colorNeutralBackground2,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: '4px',
    padding: '6px 8px',
    wordBreak: 'break-all',
  },
  // Yellow callout banner shown above the obligations table when filing-health
  // signals "needs attention" (overdue, never-filed, or has an unresolved note).
  followUpBanner: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: tokens.spacingHorizontalM,
    padding: tokens.spacingVerticalM,
    backgroundColor: tokens.colorPaletteYellowBackground1,
    border: `1px solid ${tokens.colorPaletteYellowBorder1}`,
    borderRadius: tokens.borderRadiusMedium,
  },
  followUpIcon: {
    fontSize: '24px',
    color: tokens.colorPaletteDarkOrangeForeground1,
    flexShrink: 0,
  },
  followUpBody: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  followUpTitle: {
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  followUpDetails: {
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground2,
    whiteSpace: 'pre-wrap',
  },
  notesCard: {
    width: '100%',
  },
  notesHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: tokens.spacingVerticalM,
    paddingBottom: tokens.spacingVerticalS,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    gap: tokens.spacingHorizontalM,
    flexWrap: 'wrap',
  },
  noteComposer: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    padding: tokens.spacingVerticalM,
    backgroundColor: tokens.colorNeutralBackground2,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    marginBottom: tokens.spacingVerticalM,
  },
  noteComposerRow: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    alignItems: 'center',
    flexWrap: 'wrap',
    justifyContent: 'flex-end',
  },
  noteComposerKind: {
    minWidth: '160px',
    marginRight: 'auto',
  },
  notesTimeline: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  noteItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    padding: tokens.spacingVerticalS,
    borderLeft: `3px solid ${tokens.colorBrandStroke2}`,
    paddingLeft: tokens.spacingHorizontalM,
  },
  noteItemMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    flexWrap: 'wrap',
  },
  noteItemAuthor: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    color: tokens.colorNeutralForeground2,
    fontWeight: tokens.fontWeightSemibold,
  },
  noteItemBody: {
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground1,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  // Edit/Delete affordances in the meta row. The first one carries an auto-margin so
  // it pushes both buttons to the right edge; subsequent ones tile next to it without
  // their own offset.
  noteItemEdit: {
    marginLeft: 'auto',
    minWidth: '24px',
    width: '24px',
    height: '24px',
    padding: 0,
  },
  noteItemDelete: {
    minWidth: '24px',
    width: '24px',
    height: '24px',
    padding: 0,
  },
  noteEditForm: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  noteEditActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: tokens.spacingHorizontalS,
  },
});


export default function StateTaxClientDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const linkTo = useRouterNavigate();
  const styles = useStyles();
  const { t } = useTranslation();
  const { data: client, isLoading, isError, error } = useStateTaxClient(id);

  return (
    <div className={styles.page}>
      <div className={styles.backRow}>
        <Button
          as="a"
          appearance="subtle"
          icon={<ArrowLeftRegular />}
          {...realLinkProps('/clients', { navigate: linkTo })}
        >
          {t('common.backToClients')}
        </Button>
      </div>

      {isLoading && <Spinner label={t('clientDetailPage.loading')} />}

      {isError && (
        <MessageBar intent="error">
          <MessageBarBody>
            {apiErrorMessage(error, t, 'clientDetailPage.loadError')}
          </MessageBarBody>
        </MessageBar>
      )}

      {!isLoading && !isError && client && (
        <DetailContent client={client} linkTo={linkTo} />
      )}
    </div>
  );
}

// Months between today and an ISO date. Used to drive the follow-up banner.

function DetailContent({
  client,
  linkTo,
}: {
  client: StateTaxClientSummary;
  /** The router behind this page's real links (realLinkProps' `navigate`). */
  linkTo: (path: string) => void;
}) {
  const styles = useStyles();
  const { t } = useTranslation();
  const dash = t('common.dash');
  const { data: filingHealth } = useFilingHealthByClient();

  const subtitle = client.ownerName
    ? t('clientDetailPage.subtitleAssigned', { owner: client.ownerName })
    : t('clientDetailPage.subtitleUnassigned');

  // Decide whether the follow-up banner should show. Three triggers:
  //   1. Never filed — likely a new client awaiting paperwork (e.g. GNM2 Solutions)
  //   2. Stale by 3+ months past the expected cadence (using a generous threshold so
  //      monthly-filing-due-soon clients don't all get the banner)
  //   3. Has notes that look like an unresolved follow-up
  const health = filingHealth?.[client.stateTaxClientId];
  const lastFiledMonths = health?.lastFiledMonth ? monthsAgo(health.lastFiledMonth) : Infinity;
  const neverFiled = !health?.lastFiledMonth;
  const veryStale = lastFiledMonths > 6; // unambiguous follow-up, not just one cycle late
  const showFollowUpBanner = (neverFiled || veryStale) && !!client.notes?.trim();

  const contactRows: FieldRow[] = [
    { label: t('clientDetailPage.fields.email'), value: client.email },
    { label: t('clientDetailPage.fields.phone'), value: client.phone },
    { label: t('clientDetailPage.fields.address'), value: client.address },
    { label: t('clientDetailPage.fields.owner'), value: client.ownerName },
  ];

  const identifierRows: FieldRow[] = [
    { label: t('clientDetailPage.fields.ein'), value: client.ein },
    { label: t('clientDetailPage.fields.salesTaxCertificate'), value: client.salesTaxCertificate },
    { label: t('clientDetailPage.fields.businessPartnerNumber'), value: client.businessPartnerNumber },
    { label: t('clientDetailPage.fields.reemploymentTaxNumber'), value: client.reemploymentTaxNumber },
    { label: t('clientDetailPage.fields.corporateDocumentNumber'), value: client.corporateDocumentNumber },
  ];

  return (
    <>
      <div className={styles.header}>
        <ClientIdentityBlock
          name={client.accountName}
          fictitiousName={client.fictitiousName}
          accountId={client.accountId}
          stateTaxClientId={client.stateTaxClientId}
        >
          <Text className={styles.title}>
            {client.accountName || t('clientDetailPage.unnamedAccount')}
          </Text>
        </ClientIdentityBlock>
        <Text className={styles.subtitle}>{subtitle}</Text>
      </div>

      {showFollowUpBanner && (
        <div className={styles.followUpBanner}>
          <AlertRegular className={styles.followUpIcon} />
          <div className={styles.followUpBody}>
            <Text className={styles.followUpTitle}>
              {neverFiled
                ? t('clientDetailPage.followUp.neverFiled')
                : t('clientDetailPage.followUp.lastFiled', { count: lastFiledMonths })}
            </Text>
            <Text className={styles.followUpDetails}>{client.notes}</Text>
          </div>
        </div>
      )}

      <div className={styles.cardGrid}>
        <div className="card">
          <Text className={styles.cardHeading}>{t('clientDetailPage.sections.contact')}</Text>
          <FieldList rows={contactRows} dash={dash} />
        </div>

        <div className="card">
          <Text className={styles.cardHeading}>{t('clientDetailPage.sections.identifiers')}</Text>
          <IdentifierList rows={identifierRows} dash={dash} />
        </div>
      </div>

      <ObligationsCard
        stateTaxClientId={client.stateTaxClientId}
        defaultInformationSource={client.locationOfInformationLabel}
        clientName={client.accountName}
      />

      <FilingActivityCard stateTaxClientId={client.stateTaxClientId} clientName={client.accountName} />

      <BillingCard stateTaxClientId={client.stateTaxClientId} />

      <ObligationEventsCard stateTaxClientId={client.stateTaxClientId} />

      <CommunicationsCard stateTaxClientId={client.stateTaxClientId} />

      <NotesCard stateTaxClientId={client.stateTaxClientId} />

      <div className={styles.actions}>
        <Button
          as="a"
          appearance="primary"
          icon={<TaskListSquareLtrRegular />}
          {...realLinkProps(`/compliance/clients/${client.stateTaxClientId}/obligations`, { navigate: linkTo })}
        >
          {t('clientDetailPage.actions.manageObligations')}
        </Button>
        <Button
          as="a"
          icon={<DocumentBulletListRegular />}
          {...realLinkProps(`/compliance/tasks?clientId=${encodeURIComponent(client.stateTaxClientId)}`, { navigate: linkTo })}
        >
          {t('clientDetailPage.actions.viewTasks')}
        </Button>
      </div>
    </>
  );
}

